// noinspection JSUnusedGlobalSymbols

import type { ApiService } from "~/services/ApiService";
import type {
  ActivityLog,
  BatchRemindersResponse,
  DuplicateActionEnum,
  FlowStateResponse,
  HistoryEvent,
  Item,
  ItemPaginationResponseObject,
  ItemReminder,
  ItemWidgetResponse,
  MoveInvoiceRequest,
  Note, PdfFileResponse,
  ReasonChoice,
  ReasonTypes,
  RemindItemRequest,
  RestoreInvoiceRequest,
  SendToUserRequest
} from '~~/api/models';

import type { FilterPill } from "~/components/documents/utils";

const defaultPagination = 25;

export class ItemService {
  private readonly apiService: ApiService;

  constructor(apiService: ApiService) {
    this.apiService = apiService;
  }
  async findItem(itemId: string, fields: string | null = null, widgetId: string | null = null): Promise<Item | null> {
    return this.apiService.get(`/api/invoice/find/${itemId}`, {
      headers: {
        "X-TENANT-ID": this.apiService.tenants.join(";"),
        "X-WIDGET-ID": widgetId || this.apiService.activeWidget?.normalizedName || ""
      },
      query: {
        fields
      }
    });
  }

  async findItemAndWidget(itemId: string, tenantId: string | null = null, fields: string | null = null): Promise<ItemWidgetResponse> {
    return this.apiService.get(`/api/invoice/find/widget/${itemId}`, {
      headers: {
        "X-TENANT-ID": tenantId || this.apiService.tenants.join(";")
      },
      query: {
        fields
      }
    });
  }

  async getItem(itemId: string, fields: string | null = null): Promise<Item | null> {
    if (!this.apiService.activeTenant)
      throw new Error("Missing active tenant");

    if (!this.apiService.activeWidget)
      throw new Error("Missing active widget");

    return this.apiService.get(`/api/invoice/${itemId}`, {
      headers: {
        "X-TENANT-ID": this.apiService.activeTenant,
        "X-WIDGET-ID": this.apiService.activeWidget?.normalizedName || ""
      },
      query: {
        fields
      }
    });
  }

  async isItemAccessible(itemId: string): Promise<boolean> {
    if (!this.apiService.activeTenant)
      throw new Error("Missing active tenant");

    if (!this.apiService.activeWidget)
      throw new Error("Missing active widget");

    return this.apiService.get(`/api/invoice/${itemId}/isaccessible`, {
      headers: {
        "X-TENANT-ID": this.apiService.activeTenant,
        "X-WIDGET-ID": this.apiService.activeWidget?.normalizedName || ""
      }
    });
  }

  async updateItem(itemId: string, item: Partial<Item>, fields: string | null = null): Promise<Item | null> {
    if (!this.apiService.activeTenant)
      throw new Error("Missing active tenant");

    let signal = null;

    if (this.apiService.AbortController) {
      signal = this.apiService.AbortController.signal;
    }

    return this.apiService.put(`/api/invoice/${itemId}`, {
      body: item,
      headers: {
        "X-TENANT-ID": this.apiService.activeTenant,
        "X-WIDGET-ID": this.apiService.activeWidget?.normalizedName || ""
      },
      signal: signal,
      query: {
        fields
      }
    });
  }

  async deleteItem(itemId: string): Promise<boolean | null> {
    if (!this.apiService.activeTenant)
      throw new Error("Missing active tenant");

    if (!this.apiService.activeWidget)
      throw new Error("Missing active widget");

    return this.apiService.delete(`/api/invoice/${itemId}`, {
      headers: {
        "X-TENANT-ID": this.apiService.activeTenant,
        "X-WIDGET-ID": this.apiService.activeWidget?.normalizedName || ""
      }
    });
  }

  async getItems(page: number = 1, pageSize: number = defaultPagination, fields: string | null = null, filters: FilterPill[] = [], sort: string | null = null) {
    const companyIds = filters
      .filter(f => f.type === "company")
      .map(f => f.value);
    const tenantIds = companyIds.length > 0
      ? companyIds
      : this.apiService.tenants;

    return this.apiService.get("/api/invoice/all", {
      params: {
        page,
        pageSize,
        filters: filters.length > 0 ? JSON.stringify(filters) : undefined,
        fields,
        sort
      },
      headers: {
        "x-tenant-id": tenantIds.join(";"),
        "x-widget-id": this.apiService.activeWidget?.normalizedName || ""
      }
    });
  }

  /**
   * @deprecated Use 'getItems' instead.
   */
  async getAllItems(page: number = 1, pageSize: number = defaultPagination, fields: string | null = null): Promise<ItemPaginationResponseObject> {
    return this.apiService.get("/api/invoice/all", {
      params: {
        page,
        pageSize,
        fields
      },
      headers: {
        "x-tenant-id": this.apiService.tenants.join(";"),
        "x-widget-id": this.apiService.activeWidget?.normalizedName || ""
      }
    });
  }

  /**
   * @deprecated Use 'getItems' instead.
   */
  async getFilteredItems(page: number = 1, pageSize: number = defaultPagination, filters: {
    value: string,
    type: string,
    label: string
  }[], fields: string | null = null): Promise<ItemPaginationResponseObject> {
    // The filters could include a type of "company" where the value will be some company serial number (tenantid). If this is the case, we should remove all of the tenantIds that are not in the filters with type "company"
    const tenants = filters.filter(filter => filter.type === "company").map(filter => filter.value);
    let tenantIds = this.apiService.tenants.filter(tenantId => tenants.includes(tenantId));

    if (tenantIds.length === 0)
      tenantIds = this.apiService.tenants;

    return this.apiService.get("/api/invoice/all", {
      params: {
        page,
        pageSize,
        filters: JSON.stringify(filters),
        fields
      },
      headers: {
        "x-tenant-id": tenantIds.join(";"),
        "x-widget-id": this.apiService.activeWidget?.normalizedName || ""
      }
    });
  }

  async getBatchItems(batchId: string): Promise<BatchRemindersResponse[]> {
    return this.apiService.get<BatchRemindersResponse[]>(`/api/invoice/batch/${batchId}`);
  }

  async getReasonChoices(reasonType: ReasonTypes): Promise<ReasonChoice[]> {
    if (!this.apiService.activeTenant)
      throw new Error("Missing active tenant");

    return this.apiService.get<ReasonChoice[]>(`/api/invoice/reasons/${reasonType}`, {
      headers: {
        "X-TENANT-ID": this.apiService.activeTenant,
        "X-WIDGET-ID": this.apiService.activeWidget?.normalizedName || ""
      }
    });
  }

  async getReminders(itemIds: string[]): Promise<ItemReminder[]> {
    return this.apiService.post<ItemReminder[]>("/api/invoice/reminders", {
      body: JSON.stringify(itemIds),
      headers: {
        "Content-Type": "application/json"
      }
    });
  }

  async getItemRemindersForUser(itemId: string, userId: string): Promise<ItemReminder[]> {
    return this.apiService.get(`/api/invoice/${itemId}/reminders/${userId}`);
  }

  async sendReminder(RemindItemRequest: RemindItemRequest[] | RemindItemRequest) {
    return this.apiService.post("/api/invoice/remind", {
      body: RemindItemRequest
    });
  }

  async sendReminderToAllAssignees(returnUrl: string, filters: FilterPill[] | null = null) {
    const params = filters ? {
      filters: JSON.stringify(filters)
    } : null;

    return this.apiService.post("/api/invoice/remindall", {
      params: {
        ...params
      },
      headers: {
        "x-tenant-id": this.apiService.tenants.join(";")
      },
      body: {
        returnUrl
      }
    });
  }

  async getItemDocuments(itemId: string, tenantId: string | null = null) {
    const resolvedTenantId = tenantId ?? this.apiService.activeTenant;

    if (!resolvedTenantId)
      throw new Error("Missing active tenant");

    return this.apiService.get(`/api/item/${itemId}/documents`, {
      headers: {
        "x-tenant-id": resolvedTenantId
      }
    });
  }

  async approveItem(itemId: string, force: boolean = false): Promise<void> {
    await this.apiService.post(`/api/invoice/${itemId}/flow/approve`, {
      headers: {
        "x-tenant-id": this.apiService.activeTenant || "",
        "x-widget-id": this.apiService.activeWidget?.normalizedName || ""
      },
      query: {
        forceOptionalValidations: force
      }
    });
  }

  async forwardToUser(itemId: string, sendToUserRequest: SendToUserRequest): Promise<void> {
    await this.apiService.post(`/api/invoice/${itemId}/flow/sendtouser`, {
      body: sendToUserRequest,
      headers: {
        "x-tenant-id": this.apiService.activeTenant || "",
        "x-widget-id": this.apiService.activeWidget?.normalizedName || ""
      }
    });
  }

  async declineItem(itemId: string, tenant: string, reasonMessage: string): Promise<void> {
    await this.apiService.post(`/api/invoice/${itemId}/flow/decline`, {
      body: {
        reasonMessage
      },
      headers: {
        "x-tenant-id": tenant,
        "x-widget-id": this.apiService.activeWidget?.normalizedName || ""
      }
    });
  }

  async getFlowState(itemId: string, tenant: string): Promise<FlowStateResponse> {
    return this.apiService.get(`/api/invoice/${itemId}/flow/state`, {
      headers: {
        "x-tenant-id": tenant,
        "x-widget-id": this.apiService.activeWidget?.normalizedName || ""
      }
    });
  }

  async moveItem(itemId: string, request: MoveInvoiceRequest): Promise<boolean> {
    if (!this.apiService.activeTenant)
      throw new Error("Missing active tenant");

    if (!this.apiService.activeWidget)
      throw new Error("Missing active widget");

    return this.apiService.post<boolean>(`/api/invoice/${itemId}/move`, {
      headers: {
        "X-TENANT-ID": this.apiService.activeTenant,
        "X-WIDGET-ID": this.apiService.activeWidget.normalizedName || ""
      },
      body: request
    });
  }

  async restoreItem(itemId: string, request: RestoreInvoiceRequest): Promise<boolean> {
    if (!this.apiService.activeTenant)
      throw new Error("Missing active tenant");

    if (!this.apiService.activeWidget)
      throw new Error("Missing active widget");

    return this.apiService.post<boolean>(`/api/invoice/${itemId}/restore`, {
      headers: {
        "X-TENANT-ID": this.apiService.activeTenant,
        "X-WIDGET-ID": this.apiService.activeWidget.normalizedName || ""
      },
      body: request
    });
  }

  async startFlow(itemId: string, force: boolean = false): Promise<void> {
    await this.apiService.post(`/api/invoice/${itemId}/flow/start`, {
      headers: {
        "x-tenant-id": this.apiService.activeTenant || "",
        "x-widget-id": this.apiService.activeWidget?.normalizedName || ""
      },
      query: {
        forceOptionalValidations: force
      }
    });
  }

  async addComment(itemId: string, comment: string, tenant: string): Promise<Note> {
    return this.apiService.post(`/api/comment/${itemId}/add`, {
      headers: {
        "x-tenant-id": tenant,
        "x-widget-id": this.apiService.activeWidget?.normalizedName || ""
      },
      body: {
        comment: comment
      }
    });
  }

  async getItemActivities(itemId: string, tenant: string): Promise<ActivityLog[]> {
    return this.apiService.get(`/api/invoice/${itemId}/activities`, {
      headers: {
        "x-tenant-id": tenant,
        "x-widget-id": this.apiService.activeWidget?.normalizedName || ""
      }
    });
  }

  async getItemHistoryEvents(itemId: string, tenant: string): Promise<HistoryEvent[]> {
    return this.apiService.get(`/api/invoice/${itemId}/historyevents`, {
      headers: {
        "x-tenant-id": tenant,
        "x-widget-id": this.apiService.activeWidget?.normalizedName || ""
      }
    });
  }

  async deleteAttachment(itemId: string, documentId: string): Promise<boolean> {
    return this.apiService.delete(`/api/invoice/${itemId}/attachment/${documentId}`, {
      headers: {
        "x-tenant-id": this.apiService.activeTenant || "",
        "x-widget-id": this.apiService.activeWidget?.normalizedName || ""
      }
    });
  }

  async getDuplicates(itemId: string): Promise<Item[] | null> {
    return this.apiService.get(`/api/invoice/${itemId}/duplicates/`, {
      headers: {
        "x-tenant-id": this.apiService.activeTenant || "",
        "x-widget-id": this.apiService.activeWidget?.normalizedName || ""
      }
    });
  }

  async updateDuplicateStatus(itemId: string, duplicateActionEnum: DuplicateActionEnum): Promise<boolean | null> {
    if (!this.apiService.activeTenant)
      throw new Error("Missing active tenant");

    if (!this.apiService.activeWidget)
      throw new Error("Missing active widget");

    const duplicateActionRequest = {
      action: duplicateActionEnum
    };
    return this.apiService.post(`/api/invoice/${itemId}/duplicates/update`, {
      headers: {
        "X-TENANT-ID": this.apiService.activeTenant,
        "X-WIDGET-ID": this.apiService.activeWidget?.normalizedName || ""
      },
      body: JSON.stringify(duplicateActionRequest)
    });
  }

  async getPdfFile(itemId: string) : Promise<PdfFileResponse | null>
  {
    return this.apiService.get(`/api/invoice/${itemId}/pdffile`, {
      headers: {
        "x-tenant-id": this.apiService.activeTenant || "",
        "x-widget-id": this.apiService.activeWidget?.normalizedName || ""
      }
    });
  }
}